<template>
  <v-layout row justify-center>
    <v-flex xs6>
      <v-dialog v-model="cloneDialog" max-width="1000px">
        <v-card>
          <v-card-title class="headline font-weight-light">{{
              messages.title.CLONE_RECORD
          }}</v-card-title>
          <v-card-text>
            <v-alert color="success" icon="check_circle" value="true" v-if="isCloneSuccess && cloneResponseMsg !== ''">
              <span v-html="cloneResponseMsg"></span>
            </v-alert>
            <v-alert color="error" icon="warning" value="true" v-if="!isCloneSuccess && cloneResponseMsg !== ''">
              <span v-html="cloneResponseMsg"></span>
            </v-alert>
            <!-- Selected Record -->
            <v-layout row wrap>
              <v-flex xs12>
                <v-data-table :headers="headers" :items="recordsToClone" :loading="cloningInProgress" hide-actions
                  disable-initial-sort>
                  <!-- Items -->
                  <template slot="items" slot-scope="props">
                    <tr>
                      <td class="text-xs-left">{{ props.item.state }}</td>
                      <td class="text-xs-left">{{ props.item.title }}</td>
                      <td class="text-xs-left">{{ props.item.paid }}</td>
                      <td class="text-xs-left">{{ props.item.provider }}</td>
                      <td class="text-xs-left">{{ props.item.manifestId }}</td>
                      <td class="text-xs-left">
                        {{ props.item.metadataVersion }}
                      </td>
                      <td class="text-xs-left">
                        {{ getLocalDateTime(props.item.updated) }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-flex>
            </v-layout>
            <!-- Clones selection -->
            <v-layout row wrap class="pt2" v-if="!isCloneSuccess">
              <v-flex xs2>
                <v-subheader>{{ messages.label.NUMBER_OF_CLONES }}</v-subheader>
              </v-flex>
              <v-flex xs1>
                <v-select v-bind:items="numberOfClonesList" outline v-model="numberOfClones" label="Select" single-line>
                </v-select>
              </v-flex>
            </v-layout>
            <!-- Cloned Records -->
            <v-layout row wrap v-else>
              <v-flex xs12>
                <v-subheader>{{ messages.label.CLONED_RECORDS }}</v-subheader>
              </v-flex>
              <v-flex xs12>
                <v-data-table :headers="headers" :items="clonedRecords" hide-actions disable-initial-sort>
                  <!-- Items -->
                  <template slot="items" slot-scope="props">
                    <tr>
                      <td class="text-xs-left">{{ props.item.state }}</td>
                      <td class="text-xs-left">{{ props.item.title }}</td>
                      <td class="text-xs-left">{{ props.item.paid }}</td>
                      <td class="text-xs-left">{{ props.item.provider }}</td>
                      <td class="text-xs-left">{{ props.item.manifestId }}</td>
                      <td class="text-xs-left">
                        {{ props.item.metadataVersion }}
                      </td>
                      <td class="text-xs-left">
                        {{ getLocalDateTime(props.item.updated) }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" v-if="isCloneSuccess" @click.native="doneCloning()">{{ messages.label.DONE }}</v-btn>
            <div v-else>
              <v-btn color="grey lighten-1 white--text" @click.native="cancelCloning()">{{ messages.label.CANCEL }}
              </v-btn>
              <v-btn color="primary" :loading="cloningInProgress" @click.native="cloneProcessingRecord()">{{
                  messages.label.CLONE
              }}</v-btn>
            </div>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import PreIngestService from "@/PreIngest/PreIngestService"
import * as preIngestDataService from "@/PreIngest/PreIngestDataService"
import * as utils from "@/Shared/utils"
import {
  VOD_SYCN_PREINGEST_DATA_SERVICE_URL,
  VOD_SYCN_PREINGEST_WEB_SERVICE_URL
} from "@/store/constants/getter-types"
import messages from "@/PreIngest/messages.json"

export default {
  name: "CloneRecord",
  data() {
    return {
      recordsToClone: [],
      clonedRecordIds: [],
      clonedRecords: [],
      selectedRecord: {},
      cloneDialog: false,
      numberOfClonesList: [1, 2, 3, 4, 5],
      numberOfClones: 1,
      cloneResponseMsg: "",
      isCloneSuccess: false,
      cloningInProgress: false,
      headers: this.getTableHeaders()
    }
  },
  computed: {
    ...mapState(["mpxParentAccount"]),
    ...mapGetters([
      VOD_SYCN_PREINGEST_WEB_SERVICE_URL,
      VOD_SYCN_PREINGEST_DATA_SERVICE_URL
    ])
  },
  methods: {
    /**
     * openCloneDialog - Show dialog to begin cloning process
     *
     * @param  {object} processingRecord selected processing record
     */
    openCloneDialog(processingRecord) {
      this.selectedRecord = processingRecord
      this.resetData()
      this.recordsToClone.push(this.selectedRecord)
      this.cloneDialog = true
    },
    /**
     * cancelCloning - Close the cloning dialog
     */
    cancelCloning() {
      this.cloneDialog = false
      this.resetData()
    },
    /**
     * resetData - Cleanup data. required to be done before opening / closing the modal
     */
    resetData() {
      this.recordsToClone = []
      this.clonedRecords = []
      this.clonedRecordIds = []
      this.cloneResponseMsg = ""
      this.numberOfClones = 1
      this.isCloneSuccess = false
    },
    /**
     * cloneProcessingRecord - Clone the selected processing record
     */
    cloneProcessingRecord() {
      this.cloningInProgress = true
      let reqBody = {
        cloneRecord: {
          processingRecordId: this.selectedRecord.id,
          numberOfClones: this.numberOfClones
        }
      }
      preIngestDataService
        .cloneProcessingRecord(reqBody, this.VOD_SYCN_PREINGEST_WEB_SERVICE_URL)
        .then(response => {
          this.isCloneSuccess = true
          let cloneRecordResponse = response.data.cloneRecordResponse
          this.clonedRecordIds = cloneRecordResponse
          this.cloneResponseMsg = this.messages.info.CLONE_SUCCESS
          //  Retrieve processing records for cloned records
          this.getClonedRecords()
        })
        .catch(() => {
          this.cloningInProgress = false
          this.isCloneSuccess = false
          //  let response = error.response.data
          this.cloneResponseMsg = this.messages.error.CLONE_FAILURE
        })
    },
    /**
     * getClonedRecords - Retrieve processing record object for newly cloned record
     */
    getClonedRecords() {
      let clonedRecordNumbers = this.$lodash.map(
        this.clonedRecordIds,
        recordId => {
          return this.$lodash.last(recordId.split("/"))
        }
      )
      let clonedRecordsString = clonedRecordNumbers.join(",")
      let queryObject = {
        byId: clonedRecordsString
      }

      preIngestDataService
        .retrieveProcessingRecords(
          queryObject,
          this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL
        )
        .then(response => {
          this.cloningInProgress = false
          if (this.$lodash.isUndefined(response.data.entries)) {
            this.clonedRecords.push(response.data)
          } else {
            this.clonedRecords = response.data.entries
          }
        })
        .catch(() => {
          this.cloningInProgress = false
          this.clonedRecords = []
        })
    },
    /**
     * doneCloning - Handle cloning completion
     */
    doneCloning() {
      this.cloneDialog = false
      let refreshReason = {
        clone: true,
        clonedRecordIds: this.clonedRecordIds
      }
      //  Refresh search results and pass data to highlight cloned records
      PreIngestService.$emit("event:refreshSearchResult", refreshReason)
    },

    /**
     * getTableHeaders - Retrieve table headers
     *
     * @return {array}  array of objects describing table columns
     */
    getTableHeaders() {
      let headers = [
        {
          text: messages.label.TH_STATE,
          align: "left",
          value: "state",
          sortable: false
        },
        {
          text: messages.label.TH_TITLE,
          align: "left",
          value: "title",
          sortable: false
        },
        {
          text: messages.label.TH_PACKAGE_ID,
          align: "left",
          value: "paid",
          sortable: false
        },
        {
          text: messages.label.TH_PROVIDER,
          align: "left",
          value: "provider",
          sortable: false
        },
        {
          text: messages.label.TH_MANIFEST_ID,
          align: "left",
          value: "manifestId",
          sortable: false
        },
        {
          text: messages.label.TH_METADATA_VERSION,
          align: "left",
          value: "metadataVersion",
          sortable: false
        },
        {
          text: messages.label.TH_LAST_UPDATED,
          align: "left",
          value: "updated",
          sortable: false
        }
      ]

      return headers
    },

    /**
     * getLocalDateTime - Retrieve local date time
     *
     * @param  {Number} dateTime unix timestamp
     * @return {string}          date time string in local time
     */
    getLocalDateTime(dateTime) {
      return utils.getLocalDateTime(dateTime)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     * */
    PreIngestService.$on("event:showCloneRecord", processingRecord => {
      this.openCloneDialog(processingRecord)
    })
  }
}
</script>

<style lang="css">
</style>



// WEBPACK FOOTER //
// src/PreIngest/ProcessingRecords/CloneRecord.vue